import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trainingStandardApi } from "./client";

type SortParams = { sortBy?: string; sortOrder?: "asc" | "desc" };

export const trainingStandardKeys = {
  hub: ["training-standard", "hub"] as const,
  sop: ["training-standard", "sop"] as const,
  sopDetail: (id: string) => ["training-standard", "sop", id] as const,
  flows: ["training-standard", "flows"] as const,
  flowDetail: (id: string) => ["training-standard", "flow", id] as const,
  standards: ["training-standard", "standards"] as const,
  standardDetail: (id: string) => ["training-standard", "standards", id] as const,
  checklists: ["training-standard", "checklists"] as const,
  checklistDetail: (id: string) => ["training-standard", "checklists", id] as const,
  kpi: ["training-standard", "kpi"] as const,
};

export function useTrainingHubQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: trainingStandardKeys.hub,
    queryFn: () => trainingStandardApi.getHubSummary(),
    enabled: options?.enabled ?? true,
  });
}

export function useSopListQuery(sortBy?: string, sortOrder?: "asc" | "desc") {
  return useQuery({
    queryKey: [...trainingStandardKeys.sop, sortBy ?? "", sortOrder ?? ""] as const,
    queryFn: () =>
      (trainingStandardApi.listSop as unknown as (
        params?: SortParams,
      ) => Promise<{ data: Array<Record<string, unknown>> }>)({ sortBy, sortOrder }),
  });
}

export function useSopDetailQuery(id: string) {
  return useQuery({
    queryKey: trainingStandardKeys.sopDetail(id),
    queryFn: () => trainingStandardApi.getSop(id),
    enabled: !!id,
  });
}

export function useFlowListQuery(sortBy?: string, sortOrder?: "asc" | "desc") {
  return useQuery({
    queryKey: [...trainingStandardKeys.flows, sortBy ?? "", sortOrder ?? ""] as const,
    queryFn: () =>
      (trainingStandardApi.listFlows as unknown as (
        params?: SortParams,
      ) => Promise<{ data: Array<Record<string, unknown>> }>)({ sortBy, sortOrder }),
  });
}

export function useFlowDetailQuery(id: string) {
  return useQuery({
    queryKey: trainingStandardKeys.flowDetail(id),
    queryFn: () => trainingStandardApi.getFlow(id),
    enabled: !!id,
  });
}

export function useStandardListQuery(sortBy?: string, sortOrder?: "asc" | "desc") {
  return useQuery({
    queryKey: [...trainingStandardKeys.standards, sortBy ?? "", sortOrder ?? ""] as const,
    queryFn: () =>
      (trainingStandardApi.listStandards as unknown as (
        params?: SortParams,
      ) => Promise<{ data: Array<Record<string, unknown>> }>)({ sortBy, sortOrder }),
  });
}

export function useStandardDetailQuery(id: string) {
  return useQuery({
    queryKey: trainingStandardKeys.standardDetail(id),
    queryFn: () => trainingStandardApi.getStandard(id),
    enabled: !!id,
  });
}

export function useChecklistListQuery(sortBy?: string, sortOrder?: "asc" | "desc") {
  return useQuery({
    queryKey: [...trainingStandardKeys.checklists, sortBy ?? "", sortOrder ?? ""] as const,
    queryFn: () =>
      (trainingStandardApi.listChecklists as unknown as (
        params?: SortParams,
      ) => Promise<{ data: Array<Record<string, unknown>> }>)({ sortBy, sortOrder }),
  });
}

export function useChecklistDetailQuery(id: string) {
  return useQuery({
    queryKey: trainingStandardKeys.checklistDetail(id),
    queryFn: () => trainingStandardApi.getChecklist(id),
    enabled: !!id,
  });
}

export function useChecklistItemMutation(checklistId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      itemId: string;
      isCompleted: boolean;
      notes?: string | null;
      photoUrl?: string | null;
    }) => trainingStandardApi.updateChecklistItem(checklistId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingStandardKeys.checklists });
      queryClient.invalidateQueries({
        queryKey: trainingStandardKeys.checklistDetail(checklistId),
      });
      queryClient.invalidateQueries({ queryKey: trainingStandardKeys.hub });
    },
  });
}

export function useCompleteChecklistMutation(checklistId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => trainingStandardApi.completeChecklist(checklistId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingStandardKeys.checklists });
      queryClient.invalidateQueries({
        queryKey: trainingStandardKeys.checklistDetail(checklistId),
      });
      queryClient.invalidateQueries({ queryKey: trainingStandardKeys.hub });
    },
  });
}

export function useChecklistExecutionNotesMutation(checklistId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { notes: string | null }) =>
      trainingStandardApi.patchChecklistExecutionNotes(checklistId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingStandardKeys.checklists });
      queryClient.invalidateQueries({
        queryKey: trainingStandardKeys.checklistDetail(checklistId),
      });
      queryClient.invalidateQueries({ queryKey: trainingStandardKeys.hub });
    },
  });
}

export function useKpiListQuery(sortBy?: string, sortOrder?: "asc" | "desc") {
  return useQuery({
    queryKey: [...trainingStandardKeys.kpi, sortBy ?? "", sortOrder ?? ""] as const,
    queryFn: () =>
      (trainingStandardApi.listKpi as unknown as (
        params?: SortParams,
      ) => Promise<{ data: Array<Record<string, unknown>> }>)({ sortBy, sortOrder }),
  });
}
